import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AudioPlayerService } from '@app/services/audio-player/audio-player.service';

enum MouseZone {
	'startZone',
	'endZone',
	'line',
	'outOfBounds'
}

export interface Results {
	zeit: number;
	fehler: number;
}


interface Point {
	x: number;
	y: number;
}

@Component({
	selector: 'workflow-hot-wire',
	templateUrl: './hot-wire.component.html',
	styleUrls: ['./hot-wire.component.scss',
		'../../../../../../../workflow/components/navigator/wizard-view/wizard-view.component.scss']
})
export class HotWireComponent implements OnInit, AfterViewInit {

	@Input() src: string;

	@Output() results: EventEmitter<Results> = new EventEmitter();

	private dpcm = 36.13692864308267809768476178486; //Pixels per centimeter @1080p/24", under the assumption that each pixel is perfectly square.
	private _border = 20;

	private _mouseOverObject: MouseZone;
	private _previousMouseOverObject: MouseZone;

	private currentPosition: Point;
	private startPosition: Point;
	public started: boolean;
	public startTime: number;
	public finished: boolean;

	private zeit = 0;
	private fehler = 0;

	private soundStartStop = new Audio('assets/sounds/start-stop.mp3');
	private soundFehler = new Audio('assets/sounds/fehler.mp3');

	@ViewChild('canvas')
	private canvasRef: ElementRef<HTMLCanvasElement>;

	get canvas(): HTMLCanvasElement {
		return this.canvasRef.nativeElement;
	}

	@ViewChild('pathCanvas')
	private pathCanvasRef: ElementRef<HTMLCanvasElement>;

	get pathCanvas(): HTMLCanvasElement {
		return this.pathCanvasRef.nativeElement;
	}

	@ViewChild('canvasContainer')
	private canvasContainerRef: ElementRef<HTMLElement>;

	@ViewChild('image')
	private imageRef: ElementRef<HTMLImageElement>;

	constructor(private audioPlayer: AudioPlayerService) { }

	ngOnInit(): void {
	}

	ngAfterViewInit(): void {
		this.initCanvas();
	}

	@HostListener('window:resize', ['$event'])
	onResize(): void {
		this.canvasContainerRef.nativeElement.style.marginLeft = `${window.innerWidth/2 - this.canvas.width/2}px`;
	}

	get border(): number {
		return this._border;
	}

	set mouseOverObject(mouseOverObject: MouseZone) {
		this._previousMouseOverObject = this._mouseOverObject;
		this._mouseOverObject = mouseOverObject;
	}

	get mouseOverObject(): MouseZone {
		return this._mouseOverObject;
	}

	get previousMouseOverObject(): MouseZone {
		return this._previousMouseOverObject;
	}

	get position(): { x: number, y: number } {
		if (!this.currentPosition) {
			return undefined;
		}
		return {
			x: this.currentPosition.x,
			y: this.currentPosition.y,
		};
	}

	movePointerMouse($event: MouseEvent): void {
		const radius = this.dpcm * 0.25;
		this.processPixels($event.offsetX, $event.offsetY, radius);
	}

	moveTouch($event: TouchEvent): void {
		const target = <HTMLElement>$event.target;
		const parent = <HTMLElement>target.offsetParent;
		const force = $event.touches[0].force === 0 ? 1 : $event.touches[0].force;
		const offsetX = $event.touches[0].clientX - target.offsetLeft - parent.offsetLeft;
		const offsetY = $event.touches[0].clientY - target.offsetTop - parent.offsetTop+1;
		const radius = force * this.dpcm * 0.25;

		this.processPixels(Math.round(offsetX), Math.round(offsetY), radius);
		$event.preventDefault();
	}

	processPixels(xPos: number, yPos: number, radius: number): void {
		const roundedRadius = Math.round(radius);
		const pixelData = new Uint32Array(this.canvas.getContext('2d').getImageData(xPos-roundedRadius, yPos-roundedRadius, 2*roundedRadius, 2*roundedRadius).data.buffer);
		const mouseOverScore = {
			startZone: Number.MIN_SAFE_INTEGER,
			endZone: Number.MIN_SAFE_INTEGER,
			line: Number.MIN_SAFE_INTEGER,
			empty: Number.MIN_SAFE_INTEGER
		};
		for (let index = 0; index < pixelData.length; index++) {
			const red = pixelData[index] & 0xFF;
			const green = pixelData[index] >> 8 & 0xFF;
			const blue = pixelData[index] >> 16 & 0xFF;
			const alpha = pixelData[index] >> 24 & 0xFF;

			const xCoord = xPos - roundedRadius + (index % (2*roundedRadius));
			const yCoord = yPos - roundedRadius + Math.floor(index / (2*roundedRadius));

			const a = xCoord - xPos;
			const b = yCoord - yPos;
			const distanceSquared = a*a + b*b ;
			const radiusSquared = roundedRadius * roundedRadius;

			if (distanceSquared <= radiusSquared) {
				//Green
				if (red <= 50 && green > 180 && blue <= 50) {
					if (mouseOverScore.startZone === Number.MIN_SAFE_INTEGER) {
						mouseOverScore.startZone = 0;
					}
					mouseOverScore.startZone += distanceSquared / radiusSquared * 5;
				}
				//Orange
				else if (red > 150 && green > 130 && green < 170 && blue <= 50) {
					if (mouseOverScore.endZone === Number.MIN_SAFE_INTEGER) {
						mouseOverScore.endZone = 0;
					}
					mouseOverScore.endZone += distanceSquared / radiusSquared;
				}
				//Transparent
				else if (alpha === 0){
					if (mouseOverScore.empty === Number.MIN_SAFE_INTEGER) {
						mouseOverScore.empty = 0;
					}
					mouseOverScore.empty += distanceSquared / radiusSquared;
				}
				//Purple
				else {
					if (mouseOverScore.line === Number.MIN_SAFE_INTEGER) {
						mouseOverScore.line = 0;
					}
					mouseOverScore.line += distanceSquared / radiusSquared;
				}
			}

		}

		if (mouseOverScore.startZone > mouseOverScore.line && mouseOverScore.startZone > mouseOverScore.empty) {
			this.mouseOverObject = MouseZone.startZone;
		}
		else if (mouseOverScore.endZone > mouseOverScore.line && mouseOverScore.endZone > mouseOverScore.empty) {
			this.mouseOverObject = MouseZone.endZone;
		}
		else if (mouseOverScore.line > mouseOverScore.empty && mouseOverScore.line > mouseOverScore.startZone && mouseOverScore.line > mouseOverScore.endZone) {
			this.mouseOverObject = MouseZone.line;
		}
		else {
			this.mouseOverObject = MouseZone.outOfBounds;
		}
		this.processMovement(xPos, yPos, roundedRadius);
	}

	private processMovement(xPos: number, yPos: number, radius: number): void {
		if (this.finished) {
			return;
		}

		// Startposition in Startzone merken
		if (!this.started && this.mouseOverObject === MouseZone.startZone) {
			this.startPosition = {
				x: xPos,
				y: yPos,
			};
		}

		// Wechsel aus der Startzone. Testzeit beginnt
		if (!this.started && this.previousMouseOverObject === MouseZone.startZone && this.mouseOverObject !== MouseZone.startZone) {
			const ctx = this.pathCanvas.getContext('2d');
			ctx.moveTo(this.startPosition.x, this.startPosition.y);
			ctx.rect(this.startPosition.x, this.startPosition.y, 1, 1);
			this.started = true;
			this.startTime = performance.now();
			this.currentPosition = this.startPosition;
			this.audioPlayer.play(this.soundStartStop);
		}

		// Linie nach Absetzen wieder neu beginnen
		if (this.started && !this.currentPosition) {
			this.currentPosition = {
				x: xPos,
				y: yPos,
			};
			const ctx = this.pathCanvas.getContext('2d');
			ctx.moveTo(this.currentPosition.x, this.currentPosition.y);
			ctx.rect(this.currentPosition.x, this.currentPosition.y, 1, 1);
		}

		// EndZone erkennen. Test endet
		if (this.started && this.mouseOverObject === MouseZone.endZone) {
			this.currentPosition = {
				x: xPos,
				y: yPos,
			};
			this.finished = true;
			this.zeit = performance.now() - this.startTime;
			this.audioPlayer.play(this.soundStartStop);
			this.results.emit({
				zeit: this.zeit / 1000,
				fehler: this.fehler,
			});
		}

		if (this.started && this.mouseOverObject === MouseZone.line) {
			this.currentPosition = {
				x: xPos,
				y: yPos,
			};
		}

		if (this.started && this.mouseOverObject === MouseZone.outOfBounds) {
			if (this.previousMouseOverObject !== MouseZone.outOfBounds) {
				this.audioPlayer.play(this.soundFehler);
				this.fehler++;
			}
			this.currentPosition = {
				x: xPos,
				y: yPos,
			};
		}

		this.drawCurrentPosition(radius);
	}

	drawBackgroundImage(): CanvasRenderingContext2D {
		const innerWidth = this.dpcm * 16.5;
		const innerHeight = this.dpcm * 4;

		this.canvas.width = innerWidth + 2 * this.border;
		this.canvas.height = innerHeight + 2 * this.border;

		const ctx = this.canvas.getContext('2d');
		ctx.drawImage(this.imageRef.nativeElement, this.border, this.border, innerWidth, innerHeight);

		this.pathCanvas.width = this.canvas.width;
		this.pathCanvas.height = this.canvas.height;

		this.onResize();

		return ctx;
	}

	initCanvas(): void {
		this.drawBackgroundImage();
		const ctx = this.pathCanvas.getContext('2d');

		ctx.strokeStyle = '#FF0000';
		ctx.lineWidth = 3;
		ctx.beginPath();
	}

	drawCurrentPosition(radius?: number): void {
		if (this.started || this.finished) {
			const ctx = this.pathCanvas.getContext('2d');
			if (radius) {
				ctx.lineWidth = radius/2;
			}
			ctx.lineTo(this.currentPosition.x, this.currentPosition.y);
			ctx.stroke();
		}
	}

	touchEnd($event: TouchEvent): void {
		if (this.started && !this.finished) {
			this.audioPlayer.play(this.soundFehler);
			this.fehler++;
			this._previousMouseOverObject = undefined;
			this.currentPosition = undefined;
		}
	}

}
